import { getRoute } from "../core/getRoute";
import { setConfig, getConfig } from "../constants/config";
import { Route } from "./route";
import tokenService from "../../common/tokenService";

/**
 * Main class for interacting with the Symphony Aggregator using ethers v6.
 * Provides methods for routing, token management, and configuration.
 */
export class Symphony {
  /**
   * Creates a new Symphony instance.
   *
   * @param {Signer} [signer] - Optional ethers v6 signer for executing transactions
   * @param {Object} [options] - Configuration options
   * @param {string} [options.apiUrl] - Symphony API URL
   * @param {number} [options.chainId] - Chain ID (default: 1329 for SEI)
   * @param {string} [options.chainName] - Chain name (default: "sei")
   * @param {string} [options.rpcUrl] - RPC URL
   * @param {Object} [options.tokens] - Token list override
   */
  constructor({ signer = undefined, options = {} } = {}) {
    this.signer = signer;
    setConfig(options);

    // Proactively warm token cache in background
    this._warmTokenCache();
  }

  /**
   * Proactively fetch token list to warm cache (non-blocking)
   * @private
   */
  _warmTokenCache() {
    try {
      const config = getConfig();
      const {
        chainId,
        additionalTokens,
        overrideDefaultTokens,
        nativeAddress,
      } = config;

      // Start background fetch - don't await to keep constructor non-blocking
      tokenService
        .getAsyncTokenList(
          chainId,
          additionalTokens,
          overrideDefaultTokens,
          nativeAddress
        )
        .catch((error) => {
          // Silent error handling - don't break initialization
          console.warn("Background token list fetch failed:", error.message);
        });
    } catch (error) {
      // Silent error handling for cache warming
      console.warn("Token cache warming failed:", error.message);
    }
  }

  /**
   * Connects a signer to the Symphony instance.
   * @param {Signer} signer - The ethers v6 signer to connect
   */
  connectSigner(signer) {
    this.signer = signer;
  }

  /**
   * Disconnects the current signer.
   */
  disconnectSigner() {
    this.signer = undefined;
  }

  /**
   * Gets the best swap route for a token pair.
   *
   * @param {string} tokenIn - Input token address ("0x0" for native SEI)
   * @param {string} tokenOut - Output token address
   * @param {string} amountIn - Input amount
   * @param {Object} [options] - Route options
   * @param {boolean} [options.isRaw=false] - If true, amounts are in raw units (wei)
   * @param {number} [options.slippage] - Custom slippage tolerance
   *
   * @returns {Promise<Route>} Route object containing swap details and execution methods
   *
   * @example
   * // Example 1: Get route for SEI -> USDC swap
   * const route = await symphony.getRoute(
   *   "0x0", // SEI
   *   "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48", // USDC
   *   "1.0" // 1 SEI
   * );
   *
   * @example
   * // Example 2: Get route with raw amounts and custom slippage
   * const route = await symphony.getRoute(
   *   "0xe30fedd158a2e3b13e9badaeabafc5516e95e8c7", // WSEI
   *   "0xdAC17F958D2ee523a2206206994597C13D831ec7", // USDT
   *   "1000000000000000000", // 1 WSEI in wei
   *   {
   *     isRaw: true,
   *     slippage: "1.0" // 1% slippage
   *   }
   * );
   */
  async getRoute(tokenIn, tokenOut, amountIn, options = {}) {
    const defaultOptions = { isRaw: false };
    const mergedOptions = { ...defaultOptions, ...options };

    const routePayload = await getRoute(
      tokenIn,
      tokenOut,
      amountIn,
      mergedOptions
    );

    return new Route(routePayload, () => this.signer, mergedOptions);
  }

  /**
   * Gets the list of supported tokens (static version).
   * @deprecated This method uses a static token list. Use `getTokenListAsync` for live data.
   * @returns {Object} Map of token addresses to token metadata
   */
  getTokenList() {
    const config = getConfig();
    const { chainId, additionalTokens, overrideDefaultTokens, nativeAddress } =
      config;

    return tokenService.getStaticTokenList(
      chainId,
      additionalTokens,
      overrideDefaultTokens,
      nativeAddress
    );
  }

  /**
   * Gets the list of supported tokens (async version with live data).
   * @returns {Promise<Object>} Map of token addresses to token metadata
   */
  async getTokenListAsync() {
    const config = getConfig();
    const { chainId, additionalTokens, overrideDefaultTokens, nativeAddress } =
      config;

    return await tokenService.getAsyncTokenList(
      chainId,
      additionalTokens,
      overrideDefaultTokens,
      nativeAddress
    );
  }

  /**
   * Checks if a token is supported by Symphony (static version).
   *
   * @param {string} tokenAddress - Token address to check
   * @deprecated This method uses a static token list. Use `isTokenListedAsync` for live data.
   * @returns {boolean} True if token is supported
   *
   * @example
   * const isSupported = symphony.isTokenListed("0xe30fedd158a2e3b13e9badaeabafc5516e95e8c7");
   * console.log(isSupported); // true for WSEI
   */
  isTokenListed(tokenAddress) {
    if (typeof tokenAddress !== "string" || !tokenAddress.startsWith("0x")) {
      console.error(
        `Cannot verify token 'tokenAddress' parameter is not valid. tokenAddress: ${tokenAddress}`
      );
      return false;
    }

    const tokens = this.getTokenList();
    const token = tokens[tokenAddress?.toString()?.toLowerCase()];
    return !!token;
  }

  /**
   * Checks if a token is supported by Symphony (async version with live data).
   *
   * @param {string} tokenAddress - Token address to check
   * @returns {Promise<boolean>} True if token is supported
   *
   * @example
   * const isSupported = await symphony.isTokenListedAsync("0xe30fedd158a2e3b13e9badaeabafc5516e95e8c7");
   * console.log(isSupported); // true for WSEI
   */
  async isTokenListedAsync(tokenAddress) {
    if (typeof tokenAddress !== "string" || !tokenAddress.startsWith("0x")) {
      console.error(
        `Cannot verify token 'tokenAddress' parameter is not valid. tokenAddress: ${tokenAddress}`
      );
      return false;
    }

    const tokens = await this.getTokenListAsync();
    const token = tokens[tokenAddress?.toString()?.toLowerCase()];
    return !!token;
  }

  /**
   * Gets the current configuration.
   * @returns {Object} Current configuration object
   */
  getConfig() {
    return getConfig();
  }

  /**
   * Updates the configuration.
   * @param {Object} options - New configuration options
   */
  setConfig(options) {
    setConfig(options);
  }
}
